/**
 * Modul "Nachrichten": President/VizePresident/Phantom (Berechtigung
 * "messages.manage", siehe Migration 0054) können hier Nachrichten
 * versenden, die im Dashboard des Empfängers erscheinen (aktuell fest
 * "fratellis" - siehe backend/src/routes/messages.ts). Diese Seite
 * zeigt zusätzlich den kompletten Verlauf aller bereits gesendeten
 * Nachrichten (bewusst für alle drei Rollen gemeinsam sichtbar, da sie
 * denselben Kanal zum selben Empfänger teilen) und erlaubt das Löschen
 * einzelner Nachrichten aus diesem Verlauf.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import type { DashboardMessage } from "../../types";

export default function MessagesPage() {
  const [messages, setMessages] = useState<DashboardMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return api.get<DashboardMessage[]>("/api/messages").then(setMessages).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/api/messages", { content: content.trim() });
      setContent("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Senden fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(message: DashboardMessage) {
    if (!confirm("Diese Nachricht wirklich löschen?")) return;
    try {
      await api.delete(`/api/messages/${message.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">✉️ Nachrichten</h1>
        <p className="text-sm text-gray-500 mt-1">Nachrichten landen direkt im Dashboard des Empfängers.</p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-3">
          {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
          <div>
            <label className="label">Nachricht an La Fratellanza Chipilo Dashboard</label>
            <textarea
              className="input"
              rows={4}
              maxLength={2000}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Nachricht eingeben…"
              required
            />
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" disabled={submitting || !content.trim()}>
              {submitting ? "Wird gesendet…" : "Senden"}
            </button>
          </div>
        </form>
      </Card>

      <div>
        <h2 className="text-xs font-display font-semibold uppercase tracking-widest text-sand-muted mb-3">Verlauf</h2>
        {loading ? (
          <Spinner />
        ) : messages.length === 0 ? (
          <Card className="py-10">
            <EmptyState message="Noch keine Nachrichten gesendet." />
          </Card>
        ) : (
          <div className="space-y-3">
            {messages.map((message) => (
              <Card key={message.id} className="border-l-2 border-l-accent">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-gray-200 whitespace-pre-wrap break-words min-w-0">{message.content}</p>
                  <button className="text-danger text-xs hover:underline shrink-0" onClick={() => handleDelete(message)}>Löschen</button>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-surface-border">
                  <span className="text-xs text-gray-500">{message.created_by_name ?? "—"}</span>
                  <span className="text-xs text-gray-500">{formatDateTime(message.created_at)}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
